/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { throttle } from '@/utils'
import { cubicBezier, direct } from '@/utils/animate'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import useScreen from './use-screen'

type TouchEventMap = Pick<
  HTMLElementEventMap,
  'touchcancel' | 'touchend' | 'touchmove' | 'touchstart' | 'click'
>

const listen = <K extends keyof TouchEventMap>(
  el: HTMLDivElement,
  eventType: K,
  listener: (this: HTMLDivElement, ev: TouchEventMap[K]) => any
) => {
  el.addEventListener(eventType, listener, false)
}

const unlisten = <K extends keyof TouchEventMap>(
  el: HTMLDivElement,
  eventType: K,
  listener: (this: HTMLDivElement, ev: TouchEventMap[K]) => any
) => {
  el.removeEventListener(eventType, listener, false)
}

const easings = {
  easeIn: cubicBezier(0.9, 0, 0.4, 1),
  easeOut: cubicBezier(0, 0, 0.5, 1),
  easeOutBack: cubicBezier(0.4, 1.34, 0.71, 1.29)
}

export type MovableRange = [
  number,
  {
    top?: number
    bottom?: number
  }
]

export enum OffsetUpdater {
  NONE,
  CSS,
  TOUCH,
  SCRIPT
}

export type OffsetDetail = {
  y: number
  progress: number
  _real_progress: number
  _updater: OffsetUpdater
}

const useDrawer = (
  collapsedOffset: number,
  onClick?: (toExpand: boolean) => void
) => {
  const { rootScale } = useScreen()
  const target = ref<HTMLDivElement>()
  const drawerHeight = ref(0)

  const isScrollable = ref(false)
  const isTouching = ref(false)
  const isDragable = ref(false)
  const isTransiting = ref(false)
  const isTransitingByCSS = ref(false)
  const hasEverMoved = ref(false)
  const startY = ref(-1)
  const startTimestamp = ref(-1)
  const lastY = ref(-1)
  const lastVelocity = ref(0)
  const lastTimestamp = ref(-1)

  const movement = ref(0)
  const minimalOffset = computed(() =>
    drawerHeight.value
      ? collapsedOffset * rootScale.value - drawerHeight.value
      : 0
  )

  const offset = computed(() => {
    const updater = isTouching.value
      ? OffsetUpdater.TOUCH
      : isTransiting.value
      ? OffsetUpdater.SCRIPT
      : isTransitingByCSS.value
      ? OffsetUpdater.CSS
      : OffsetUpdater.NONE

    if (movement.value) {
      if (movement.value > 0) {
        const lackProgress = movement.value * 25
        return {
          y: movement.value / 4,
          progress: 0,
          _real_progress: 0 - lackProgress,
          _updater: updater
        }
      }
      if (movement.value < minimalOffset.value) {
        const overMovement = movement.value - minimalOffset.value
        const overProgress = (overMovement / minimalOffset.value) * 25
        return {
          y: minimalOffset.value + overMovement / 4,
          progress: 100,
          _real_progress: 100 + overProgress,
          _updater: updater
        }
      }
      const realProgress = (movement.value / minimalOffset.value) * 100
      // console.log('realProgress', realProgress)
      return {
        y: movement.value,
        progress: realProgress,
        _real_progress: realProgress,
        _updater: updater
      }
    } else {
      return {
        y: 0,
        progress: 0,
        _real_progress: 0,
        _updater: updater
      }
    }
  })

  const updateScrollable = (scrollable: boolean) => {
    if (scrollable === isScrollable.value) return
    console.log('[[ Drawer ] Update Scrollable ]', { scrollable })
    isScrollable.value = scrollable
  }

  const transit = (
    from: number,
    to: number,
    seconds: number,
    easing: (t: number) => number
  ) => {
    isTransitingByCSS.value = false
    if (Math.abs(to - from) <= 1) {
      movement.value = to
      isTransiting.value = false
      return
    }

    isTransiting.value = true
    console.log('[[ Drawer ] TransitBySCRIPT Start ]')
    direct([from, to], seconds * 1000, {
      easingFunction: easing,
      onProgress: function (progress: any, breakFn: any) {
        movement.value = progress.currentValue
      },
      onComplete: function () {
        isTransiting.value = false
        movement.value = to
        updateScrollable(to !== 0)
        console.log('[[ Drawer ] TransitBySCRIPT Finish ]')
      },
      scheduler: requestAnimationFrame
    })
  }

  const transitByCSS = (to: number, seconds: number) => {
    isTransiting.value = false
    if (seconds) {
      console.log('[[ Drawer ] TransitByCSS Start ]')
      setTimeout(() => {
        isTransitingByCSS.value = false
        movement.value = to
        updateScrollable(to !== 0)
        console.log('[[ Drawer ] TransitByCSS Finish ]')
      }, seconds * 1000)
      isTransitingByCSS.value = true
    } else {
      isTransitingByCSS.value = false
    }
    movement.value = to
  }

  const expand = (duration = 0, easing?: (t: number) => number) => {
    isTouching.value = false
    if (easing) {
      transit(movement.value, minimalOffset.value, duration, easing)
    } else {
      transitByCSS(minimalOffset.value, duration)
    }
  }

  const collapse = (duration = 0, easing?: (t: number) => number) => {
    isTouching.value = false
    if (easing) {
      transit(movement.value, 0, duration, easing)
    } else {
      transitByCSS(0, duration)
    }
  }

  const isTargetTouchable = (
    currentTarget: HTMLElement | null
  ): false | 'scrollable' | 'clickable' | 'both' => {
    if (currentTarget === null || currentTarget === target.value) {
      return false
    }
    if (currentTarget.getAttribute('data-is-touchable') === 'both') {
      return 'both'
    }
    if (currentTarget.getAttribute('data-is-touchable') === 'scrollable') {
      return 'scrollable'
    }
    if (currentTarget.getAttribute('data-is-touchable') === 'clickable') {
      return 'clickable'
    }
    return currentTarget.parentElement
      ? isTargetTouchable(currentTarget.parentElement)
      : false
  }

  const onTouchStart = (ev: TouchEvent) => {
    const touch = ev.changedTouches[0]

    if (isTargetTouchable(ev.target as HTMLElement | null)) {
      isDragable.value = false
    } else {
      isDragable.value = true
      startTimestamp.value = ev.timeStamp
      startY.value = touch.clientY
      lastY.value = touch.clientY
      isTouching.value = true
    }

    hasEverMoved.value = false
  }

  const onTouchMove = (ev: TouchEvent) => {
    if (isTouching.value && isDragable.value) {
      const touch = ev.changedTouches[0]
      const delta = touch.clientY - lastY.value
      movement.value += delta
      lastVelocity.value = delta / (ev.timeStamp - lastTimestamp.value)
      lastTimestamp.value = ev.timeStamp
      lastY.value = touch.clientY
      hasEverMoved.value = true
    }
  }

  const onTouchEnd = (ev: TouchEvent) => {
    if (isTouching.value && isDragable.value) {
      // console.log(ev)
      if (hasEverMoved.value) {
        const endingDuration = ev.timeStamp - lastTimestamp.value
        if (endingDuration > 300 || Math.abs(lastVelocity.value) <= 0.5) {
          // 手指离开前处于长按状态，速度刹为 0，
          // 或最终速度太低，
          // 按中线分配
          if (offset.value.progress > 50) {
            expand(offset.value.progress > 75 ? 0.2 : 0.4, easings.easeOut)
          } else {
            collapse(offset.value.progress < 25 ? 0.2 : 0.4, easings.easeOut)
          }
        } else {
          // console.log(
          //   'lastVelocity',
          //   lastVelocity.value,
          //   Math.abs(lastVelocity.value)
          // )
          if (lastVelocity.value > 0) {
            if (lastVelocity.value > 0.8) {
              collapse(
                0.24,
                movement.value < 0 ? easings.easeOutBack : easings.easeOut
              )
            } else {
              collapse(0.3, easings.easeOut)
            }
          } else {
            if (lastVelocity.value < -0.8) {
              expand(
                0.24,
                movement.value > minimalOffset.value
                  ? easings.easeOutBack
                  : easings.easeOut
              )
            } else {
              expand(0.3, easings.easeOut)
            }
          }
        }
      }
    } else if (isTouching.value) {
      isTouching.value = false
    }
  }

  const onMaskClick = throttle(() => {
    if (isTransiting.value || isTransitingByCSS.value) {
      console.log('[[ Drawer ] Click Mask While Transiting ]')
    } else if (movement.value) {
      console.log('[[ Drawer ] Click Mask ]')
      collapse(0.6)
    }
  })

  const onDrawerClick = throttle((ev: MouseEvent) => {
    if (
      isTargetTouchable(ev.target as HTMLElement | null) === 'clickable' ||
      isTargetTouchable(ev.target as HTMLElement | null) === 'both'
    ) {
      console.log('[[ Drawer ] Click Touchable Element Of Drawer]')
      return
    }
    if (isTransiting.value || isTransitingByCSS.value) {
      console.log('[[ Drawer ] Click Drawer While Transiting ]')
      return
    }
    // console.log('[[ Drawer ] Click Drawer ]', {
    //   toExpand: movement.value === 0
    // })
    if (movement.value) {
      onClick?.(false)
      collapse(0.6)
    } else {
      onClick?.(true)
      expand(0.6)
    }
  })

  const free = (el: HTMLDivElement) => {
    unlisten(el, 'touchstart', onTouchStart)
    unlisten(el, 'touchmove', onTouchMove)
    unlisten(el, 'touchend', onTouchEnd)
    unlisten(el, 'touchcancel', onTouchEnd)
    unlisten(el, 'click', onDrawerClick)
  }

  watch(minimalOffset, () => {
    if (drawerHeight.value) {
      // console.log('[[ Drawer ] Update Offset Range ]', {
      //   min: minimalOffset.value,
      //   max: 0,
      //   height: drawerHeight.value
      // })
      if (
        isTransiting.value ||
        isTransitingByCSS.value ||
        movement.value === 0
      ) {
        collapse(0.6)
      } else {
        expand(0.6)
      }
    }
  })

  watch(target, (el, old) => {
    if (old) {
      console.log('[[ Drawer ] Target Changed ]', { el, old })
      free(old)
    }
    if (el) {
      drawerHeight.value = el.getBoundingClientRect().height
      listen(el, 'touchstart', onTouchStart)
      listen(el, 'touchmove', onTouchMove)
      listen(el, 'touchend', onTouchEnd)
      listen(el, 'touchcancel', onTouchEnd)
      listen(el, 'click', onDrawerClick)
    }
  })

  onUnmounted(() => {
    collapse(0)
    if (target.value) free(target.value)
  })

  return {
    target,
    offset,
    isTouching,
    isScrollable,
    isTransiting,
    onMaskClick,
    expand,
    collapse
  }
}

export default useDrawer
